import { useCallback, useEffect } from 'react'
import { ApolloQueryResult, useQuery } from '@apollo/client'
import { GET_MOMENT_OWNERS } from '@/services/MomentsService/queries'
import { useUser } from '@/services/UserService'
import { GetMomentOwnersQuery, GetMomentOwnersQueryVariables } from '@/types/codegen-federation'

const OWNER_COUNT = 10
const STORY_COUNT = 30
const ACTOR_COUNT = 5

interface UseEngagementMomentProps {
  momentId?: string | null
}

export const useEngagementMoment = ({ momentId }: UseEngagementMomentProps) => {
  const { isLoggedIn, userId } = useUser()

  const { data, loading, refetch } = useQuery<GetMomentOwnersQuery, GetMomentOwnersQueryVariables>(GET_MOMENT_OWNERS, {
    skip: !momentId,
    variables: {
      momentId: momentId as string,
      includeMyStory: isLoggedIn,
      userId: userId ?? '',
      ownerCount: OWNER_COUNT,
      actorCount: ACTOR_COUNT,
      userStoriesTake: STORY_COUNT,
    },
    errorPolicy: 'all',
  })

  const refetchMe = useCallback(
    async ({
      includeMyStory,
      momentId,
    }: Pick<GetMomentOwnersQueryVariables, 'momentId' | 'includeMyStory'>): Promise<
      ApolloQueryResult<GetMomentOwnersQuery>
    > => {
      return refetch({
        momentId: momentId as string,
        includeMyStory,
        userId: userId ?? '',
        ownerCount: OWNER_COUNT,
        actorCount: ACTOR_COUNT,
        userStoriesTake: STORY_COUNT,
      })
    },
    [userId, refetch],
  )

  const fetchMore = useCallback(
    ({ take = STORY_COUNT, skip }: { take: number; skip: number }) => {
      if (!momentId) return

      return refetch({
        momentId,
        userStoriesTake: take,
        userStoriesSkip: skip,
      })
    },
    [momentId, refetch],
  )

  useEffect(() => {
    if (!momentId) return
    refetchMe({ includeMyStory: isLoggedIn, momentId })
  }, [momentId, isLoggedIn, refetchMe])

  return {
    data,
    loading,
    refetch: refetchMe,
    fetchMore,
  }
}
